<template>
  <section class="bike-showcase-section">
    <div class="container">
      <div class="section-header">
        <div class="section-tag">
          <i :class="headerData.icon"></i>
          <span>{{ headerData.tag }}</span>
        </div>
        <h2>{{ headerData.title }}</h2>
        <p class="section-description">{{ headerData.description }}</p>
      </div>
      
      <div class="bike-services-grid">
        <div class="service-card" v-for="(service, index) in servicesData" :key="index">
          <div class="service-icon-wrapper">
            <i :class="service.icon"></i>
          </div>
          <div class="service-content">
            <h3>{{ service.title }}</h3>
            <p>{{ service.description }}</p>
            <div class="service-features">
              <span v-for="(feature, featureIndex) in service.features" :key="featureIndex" class="feature-tag">
                {{ feature }}
              </span>
            </div>
          </div>
          <div class="service-overlay">
            <div class="overlay-content">
              <h4>{{ service.title }}</h4>
              <p>{{ service.detailedDescription }}</p>
              <button class="service-btn" @click="handleServiceClick(service)">{{ buttonText }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ServiceItsShowcaseCardComp",
  props: {
    headerData: {
      type: Object,
      required: true,
      default: () => ({
        icon: "fas fa-bicycle",
        tag: "OUR BIKE SERVICES",
        title: "Bicycle Care Excellence",
        description: "Professional bicycle services with precision and passion for cycling"
      })
    },
    servicesData: {
      type: Array,
      required: true,
      default: () => []
    },
    buttonText: {
      type: String,
      default: "Learn More"
    }
  },
  methods: {
    handleServiceClick(service) {
      this.$emit('service-click', service);
    }
  }
};
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Bike Showcase Section */
.bike-showcase-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #0a0f1c 0%, #1a2332 50%, #0a0f1c 100%);
  color: #fff;
  position: relative;
  overflow: hidden;
}

.bike-showcase-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, 
    rgba(0, 255, 150, 0.05) 0%, 
    rgba(0, 255, 200, 0.03) 25%, 
    rgba(0, 255, 100, 0.05) 50%, 
    rgba(0, 255, 150, 0.03) 75%, 
    rgba(0, 255, 200, 0.05) 100%);
  pointer-events: none;
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  z-index: 2;
}

.section-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 255, 150, 0.1);
  border: 1px solid rgba(0, 255, 150, 0.3);
  padding: 8px 16px;
  border-radius: 25px;
  margin-bottom: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #00ff96;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 15px 0;
  font-family: 'Archivo', sans-serif;
}

.section-description {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 30px 0;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.bike-services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.service-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 150, 0.2);
  border-radius: 15px;
  padding: 30px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
}

.service-card:hover {
  transform: translateY(-10px);
  border-color: rgba(0, 255, 150, 0.4);
  box-shadow: 0 20px 40px rgba(0, 255, 150, 0.1);
}

.service-icon-wrapper {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #00ff96 0%, #00ff64 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: all 0.3s ease;
}

.service-card:hover .service-icon-wrapper {
  transform: scale(1.1);
}

.service-icon-wrapper i {
  font-size: 24px;
  color: #000;
}

.service-content h3 {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 15px 0;
  font-family: 'Archivo', sans-serif;
}

.service-content p {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 20px 0;
  line-height: 1.6;
}

.service-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.feature-tag {
  background: rgba(0, 255, 150, 0.1);
  color: #00ff96;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(0, 255, 150, 0.2);
}

.service-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(0, 0, 0, 0.9) 0%, 
    rgba(0, 0, 0, 0.7) 50%, 
    rgba(0, 0, 0, 0.9) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  opacity: 0;
  transition: all 0.3s ease;
  border-radius: 15px;
}

.service-card:hover .service-overlay {
  opacity: 1;
}

.overlay-content {
  text-align: center;
}

.overlay-content h4 {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 15px 0;
  font-family: 'Archivo', sans-serif;
}

.overlay-content p {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 25px 0;
  line-height: 1.6;
}

.service-btn {
  background: linear-gradient(135deg, #00ff96 0%, #00ff64 100%);
  color: #000;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.service-btn:hover {
  background: linear-gradient(135deg, #00ff64 0%, #00ff96 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 255, 150, 0.3);
}

/* Responsive Design */
@media (max-width: 991px) {
  .section-header h2 {
    font-size: 36px;
  }
  
  .section-description {
    font-size: 16px;
  }
  
  .bike-services-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 25px;
  }
}

@media (max-width: 767px) {
  .section-header h2 {
    font-size: 28px;
  }
  
  .section-description {
    font-size: 14px;
  }
  
  .bike-services-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .service-card {
    padding: 25px;
  }
  
  .service-content h3 {
    font-size: 20px;
  }
  
  .service-content p {
    font-size: 14px;
  }
}
</style>
